import React from "react";
import { Row, Col, Typography, Drawer } from "antd";
import { getLangList } from '@/lib/i18n';
import ResponsiveTable from '@/components/responsiveTable';
import { dateTimeFormat, convertTimestampToFormat, dateFormat } from "@/lib/dateTime";
import { startTimeToday, endTimeToday, convertToTimestamp10, getCurrentTime, getMoment } from "@/lib/dateTime";
import { sortColumn } from "@/lib/antdElementFunction";
import AnnouncementConfig from "@/config/announcement";

const { getAnnouncementInfo } = AnnouncementConfig;
const { Title } = Typography;

interface props {
  clickDrawer: any;
  selectedClassroom: string;
  semesterId: number;
}

interface state {
  announcementObjcet: any;
}

class DrawerAnnouncement extends React.Component<props, state> {
  state = {
    announcementObjcet: {} as never as any,
  };
  announcementT: any = getLangList("announcement");
  leaveT: any = getLangList("leave");
  refreshApi: any;

  componentDidMount() {
    this.getAnnouncementInfo();
    this.refreshApi = setInterval(this.getAnnouncementInfo, 1000 * 60);
  }

  componentWillUnmount() {
    clearInterval(this.refreshApi);
  }

  columns = [
    {
      title: this.announcementT.SITE_NO,
      dataIndex: "cramSeat",
      sorter: (a: any, b: any) => sortColumn(a.cramSeat, b.cramSeat),
      sortDirections: ['ascend', 'descend', 'ascend'],
      defaultSortOrder: 'ascend',
    },
    { title: this.announcementT.CN_NAME, dataIndex: "nameCn" },
    { title: this.announcementT.EN_NAME, dataIndex: "nameEn" },
    {
      title: this.announcementT.SIGN_SITUATION,
      dataIndex: "signInTime",
      render: (text: any, record: any) => this.getSignInTime(record),
    },
    { title: this.announcementT.ALLERGY, dataIndex: "allergy" },
    {
      title: this.announcementT.LUNCH,
      dataIndex: "isLunch",
      render: (text: boolean) => !!text ? 'V' : '',
    },
    {
      title: this.announcementT.SNACK,
      dataIndex: "isSnack",
      render: (text: boolean) => !!text ? 'V' : '',
    },
    { title: this.announcementT.AFTER_SCHOOL, dataIndex: "afterSchool" },
  ];

  getAnnouncementInfo = () => {
    const { selectedClassroom, semesterId } = this.props;
    const params = {
      startTime: convertToTimestamp10(startTimeToday),
      endTime: convertToTimestamp10(endTimeToday),
      classroom: selectedClassroom,
      classType: this.getClassType(),
      semesterId,
    };

    getAnnouncementInfo(params).then((res: any) => {
      const announcementObjcet = res?.value ?? {};
      this.setState({ announcementObjcet });
    });
  }

  getSignInTime = (record: any) => {
    const { signInTime, leaveType } = record;
    if (signInTime) {
      return convertTimestampToFormat(signInTime, dateTimeFormat)
    } else if (leaveType) {
      return this.translateLeaveType(leaveType);
    }
    return '-';
  }

  translateLeaveType = (leaveType: string) => {
    switch (leaveType) {
      case 'sick':
        return this.leaveT.SICK;
      case 'personal':
        return this.leaveT.PERSONAL;
      case 'other':
        return this.leaveT.OTHER;
    }
  }

  getLeaveClassName = (record: any) => {
    const { leaveType, signInTime } = record;

    if (leaveType && !signInTime) {
      return leaveType;
    } else if (this.isLate(signInTime)) {
      return 'late';
    }
    return '';
  }

  isLate = (signInTime: any) => {
    const { announcementObjcet } = this.state;
    const { classType } = announcementObjcet;
    const signInTimeMoment = getMoment(signInTime);
    const currentTime = getCurrentTime();
    const intervalA = `${currentTime.format(dateFormat)} 09:00:00`;
    const intervalB = `${currentTime.format(dateFormat)} 14:00:00`;
    const intervalC = `${currentTime.format(dateFormat)} 17:00:00`;
    let interval = `${currentTime.format(dateFormat)} 00:00:00`;

    switch (classType) {
      case 'Full Day':
        interval = intervalA;
        break;
      case 'PM':
        interval = intervalB;
        break;
      case 'EVE':
        interval = intervalC;
        break;
    }

    if (signInTime && !signInTimeMoment.isAfter(interval)) {
      return false;
    }
    return currentTime.isAfter(interval);
  }

  getClassType = () => {
    const currentTime = getCurrentTime();
    const intervalA = [`${currentTime.format(dateFormat)} 08:00:00`, `${currentTime.format(dateFormat)} 12:00:00`];
    const intervalB = [`${currentTime.format(dateFormat)} 12:00:00`, `${currentTime.format(dateFormat)} 17:00:00`];
    const intervalC = [`${currentTime.format(dateFormat)} 17:00:00`, `${currentTime.format(dateFormat)} 19:30:00`];

    if (currentTime.isBetween(intervalA[0], intervalA[1])) {
      return 'Full Day';
    } else if (currentTime.isBetween(intervalB[0], intervalB[1])) {
      return 'Full Day,PM';
    } else if (currentTime.isBetween(intervalC[0], intervalC[1])) {
      return 'EVE';
    }
    return '';
  }

  getDrawerTitle = () => {
    const { announcementObjcet } = this.state;
    const { className, nameEn, classType, classroom } = announcementObjcet;

    return (
      <Row gutter={[0, 6]}>
        <Col span={24}>
          <Title>
            {(className && classType) && (`${className ?? '-'} (${classType ?? '-'})`)}
          </Title>
        </Col>
        <Col span={24}>
          {nameEn && (`${this.announcementT.TEACHER}: ${nameEn}`)}
        </Col>
        <Col span={24}>
          {classroom && (`${this.announcementT.CLASSROOM}: ${classroom}`)}
        </Col>
      </Row>
    );
  };

  render() {
    const { clickDrawer } = this.props;
    const { announcementObjcet } = this.state;
    const { studentDetails = [] } = announcementObjcet;

    return (
      <Drawer
        title={this.getDrawerTitle()}
        placement="bottom"
        height="100vh"
        onClose={() => clickDrawer()}
        visible={true}
        className="announcement-drawer"
      >
        <ResponsiveTable
          dataSource={studentDetails}
          columns={this.columns}
          rowClassName={this.getLeaveClassName}
        />
      </Drawer>
    );
  }
}

export default DrawerAnnouncement;